import type { HubSessionConfig } from './ris/hub-session';

export type LoadMode = 'fast' | 'parity';

export interface ModalityDisplay {
  defaultFormat: 'dicom' | 'ref';
  presetWindows: Record<string, [width: number, center: number]>;
}

/**
 * A DICOMweb gateway a launch link may name with `?gateway=<name>`. The link only names it; the
 * address comes from this deployment's config, so a forged link can never send the ticket elsewhere.
 */
export interface GatewayConfig {
  dicomwebRoot: string;
  /** WADO-URI root; without one the gateway is WADO-RS only and the viewer loads in `fast` mode. */
  wadoUriRoot?: string | null;
  /** Whether the ris workspace routes (annotations, key images, preferences) exist for this gateway. */
  workspace: boolean;
}

export interface RuntimeConfig {
  centerName: string;
  /**
   * Logged-in viewing: the viewer is served on the hub's site and an `?exam=` link is opened with the
   * user's own hub session (src/ris/hub-session.ts). Absent = links carry their own ticket.
   */
  hubSession?: HubSessionConfig | null;
  /** Named gateways a launch link may pick (`?gateway=`); the top-level roots are the default one. */
  gateways?: Record<string, GatewayConfig>;
  dicomwebRoot: string;
  /** Origin (+ optional prefix) of the ris API for workspace routes; defaults to the host of dicomwebRoot. */
  risApiRoot?: string | null;
  wadoUriRoot: string;
  loadMode: LoadMode;
  magnifyFactor: number;
  display: Record<string, ModalityDisplay> & { default: ModalityDisplay };
  modalities: string[];
  // Module gates shared with the IMEXHS viewer_config.js (spec section 7). Optional: when absent,
  // commands.ts applies the IMEXHS deployment defaults.
  tools3d?: boolean;
  keyImagesModule?: boolean;
  segmentationModule?: boolean;
  hangingEnabled?: boolean;
  stowUrl?: string;
  uploadEnabled?: boolean;
  downloadDicomEnabled?: boolean;
  multiScreenOption?: boolean;
  settingsEnabled?: boolean;
  flipToolsInTopMenu?: boolean;
  advancedAnnotationsEnabled?: boolean;
  obliqueMprEnabled?: boolean;
  noAxialMpr?: boolean;
  reportsModule?: boolean;
  /** IMEXHS `diagnosticWarning`: show " No apto para uso diagnóstico ". */
  diagnosticWarning?: boolean;
  /** IMEXHS `sliceReorientation`: show series in standard orientation (display/reorient.ts). */
  sliceReorientation?: boolean;
  /** IMEXHS `mobileModule`: on a mobile device the overlay key-image star is hidden (default true). */
  mobileModule?: boolean;
  /** Abort an image / metadata request after this many seconds without data (src/net.ts; default 30). */
  requestIdleTimeoutSeconds?: number;
  /** Annotation grab distances in CSS px (src/tools/proximity.ts; IMEXHS v8 keys): move 8, handle 12, touch 24. */
  annot_move_proximity?: number;
  annot_handle_proximity?: number;
  annot_touch_proximity?: number;
}

export interface LaunchParams {
  studyUIDs: string[];
  token: string | null;
  /** The exam (accession id) to open from the hub session (`?exam=`); null on a ticket link. */
  examId: string | null;
  /** A "Compartir" link (`?share=`): opened without an account, after its access code when it has one. */
  shareId: string | null;
  mode: LoadMode;
  /** ris hanging protocol chosen when the ticket was issued (`protocolId`), if any. */
  protocolId: string | null;
  /** Gateway the link names (`?gateway=`), resolved with `withGateway`; null = the default roots. */
  gateway: string | null;
}

const TOKEN_PARAM = 'token';
const EXAM_PARAM = 'exam';
const SHARE_PARAM = 'share';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GATEWAY_NAME = /^[a-z][a-z0-9_-]{0,31}$/;

/** Name of the opener's handoff function (src/ui/multiscreen.ts CHILD_TICKET_HANDOFF). */
const CHILD_TICKET_HANDOFF = '__viewerTicketForChild';

/**
 * A child screen (`?screen=N`, opened by the main viewer window) takes the ticket from its opener,
 * so the ticket never travels in a URL. Cross-origin or missing opener → null.
 */
function ticketFromOpener(url: URL): string | null {
  if (!url.searchParams.has('screen')) return null;
  try {
    const handoff = (window.opener as Record<string, unknown> | null)?.[CHILD_TICKET_HANDOFF];
    const ticket = typeof handoff === 'function' ? (handoff as () => unknown)() : null;
    return typeof ticket === 'string' && ticket ? ticket : null;
  } catch {
    return null;
  }
}

/**
 * Reads the study to open, the viewer ticket and the load mode from the address bar.
 * Accepts the IMEXHS link shape (`?studyUID=`) and the ris ticket shape (`?StudyInstanceUIDs=`).
 * The ticket is removed from the address bar right away so it never lands in history or screenshots.
 */
export function readLaunchParams(config: RuntimeConfig): LaunchParams {
  const url = new URL(window.location.href);
  const raw = url.searchParams.get('studyUID') ?? url.searchParams.get('StudyInstanceUIDs') ?? '';
  const studyUIDs = raw.split(',').map((uid) => uid.trim()).filter(Boolean);
  const exam = url.searchParams.get(EXAM_PARAM);
  const examId = exam && UUID.test(exam) ? exam : null;
  const share = url.searchParams.get(SHARE_PARAM);
  const shareId = share && UUID.test(share) ? share : null;
  // An exam link renews its own ticket from the hub session, child screens included: never an opener's copy.
  const token = url.searchParams.get(TOKEN_PARAM) ?? (examId ? null : ticketFromOpener(url));
  if (url.searchParams.has(TOKEN_PARAM)) {
    url.searchParams.delete(TOKEN_PARAM);
    window.history.replaceState(null, '', url.toString());
  }
  const modeParam = url.searchParams.get('mode');
  const mode: LoadMode = modeParam === 'parity' || modeParam === 'fast' ? modeParam : config.loadMode;
  const protocolId = url.searchParams.get('protocolId');
  return {
    studyUIDs, token, examId, shareId, mode,
    protocolId: protocolId && /^[0-9a-f-]{36}$/i.test(protocolId) ? protocolId : null,
    gateway: url.searchParams.get('gateway'),
  };
}

/**
 * The config this launch runs with: the default roots, or those of the gateway the link names.
 * Null when the link names a gateway this deployment does not know (the caller refuses to load).
 */
export function withGateway(config: RuntimeConfig, launch: LaunchParams): { config: RuntimeConfig; launch: LaunchParams } | null {
  if (launch.gateway === null) return { config, launch };
  const gateway = GATEWAY_NAME.test(launch.gateway) ? config.gateways?.[launch.gateway] : undefined;
  if (!gateway) return null;
  const wadoUriRoot = gateway.wadoUriRoot ?? null;
  return {
    config: {
      ...config,
      dicomwebRoot: gateway.dicomwebRoot,
      wadoUriRoot: wadoUriRoot ?? '',
      risApiRoot: gateway.workspace ? risApiRootOf(gateway.dicomwebRoot) : null,
    },
    // Parity mode reads whole files over WADO-URI; a WADO-RS-only gateway always loads fast.
    launch: wadoUriRoot === null ? { ...launch, mode: 'fast' } : launch,
  };
}

export function displayFor(config: RuntimeConfig, modality: string): ModalityDisplay {
  return config.display[modality] ?? config.display.default;
}

// The ris gateways: the AQUILA-backed one (/ris/v1/dicomweb) and the core one (/ris/v1/core/dicomweb).
const GATEWAY_SUFFIX = /\/ris\/v1\/(core\/)?dicomweb\/?$/;

/**
 * Where the ris viewer routes live. Explicit `risApiRoot` wins; otherwise the ris host is the
 * one serving the DICOMweb gateway (`…/ris/v1/dicomweb`). Null when there is no ris
 * (standalone bench against a plain PACS): workspace features are then hidden.
 */
export function risApiRoot(config: RuntimeConfig): string | null {
  if (config.risApiRoot !== undefined) return config.risApiRoot;
  return risApiRootOf(config.dicomwebRoot);
}

function risApiRootOf(dicomwebRoot: string): string | null {
  return GATEWAY_SUFFIX.test(dicomwebRoot) ? dicomwebRoot.replace(GATEWAY_SUFFIX, '') : null;
}
